import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Percent, Edit } from "lucide-react";

export default function LoanCommissions() {
  const { hasPerm } = useAuth();
  const { items, total, loading, page, setPage, refetch } = useList("/loan/commissions", {});
  const edit = async (c, v) => { await api.put(`/loan/commissions/${c.id}`, v); refetch(); };
  const editFields = [
    { name: "confirmed_commission", label: "Confirmed Commission (₹)", type: "number" },
    { name: "commission_received", label: "Commission Received (₹)", type: "number", required: true },
    { name: "settlement_date", label: "Settlement Date", type: "date" },
    { name: "lender_statement_ref", label: "Lender Statement Reference" },
    { name: "payment_status", label: "Payment Status", type: "select", options: ["pending", "partially_received", "received", "disputed"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "remarks", label: "Remarks", type: "textarea", full: true },
  ];
  const columns = [
    { key: "loan_app_number", header: "Application", render: (r) => <span className="font-mono text-xs">{r.loan_app_number}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name || "—"}</span> },
    { key: "lender", header: "Lender", render: (r) => <span className="text-sm">{r.lender}</span> },
    { key: "expected_commission", header: "Expected", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-violet-600">{INR(r.expected_commission)}</span> },
    { key: "confirmed_commission", header: "Confirmed", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{INR(r.confirmed_commission)}</span> },
    { key: "commission_received", header: "Received", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-emerald-600 font-semibold">{INR(r.commission_received)}</span> },
    { key: "commission_pending", header: "Pending", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-amber-600">{INR(r.commission_pending)}</span> },
    { key: "payment_status", header: "Status", render: (r) => <StatusBadge value={r.payment_status} /> },
    ...(hasPerm("loan_commissions:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Reconcile ${r.loan_app_number}`} description="Reconcile against the verified lender statement." fields={editFields} initial={{ commission_received: r.commission_received, confirmed_commission: r.confirmed_commission }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="loan-commission-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Loan Commissions" subtitle={`${total} entries · reconcile against verified lender statements`} icon={Percent} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="loan-commission-table-row" emptyText="No loan commissions yet" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
